import { enableProdMode, importProvidersFrom, ErrorHandler } from '@angular/core';
import {
  recargarSiEsVersionVieja,
  levantarSeguroTrasArranqueEstable,
} from './app/services/recarga-por-version';

// ============================================================
// 🆕 INICIO: Aplicacion desactualizada tras un despliegue - 2026-08-31
// ============================================================
// Tras publicar una version nueva, una pestana abierta conserva en memoria el
// codigo de la ANTERIOR. Eso rompe la navegacion de dos maneras distintas, y
// hacen falta las dos comprobaciones porque ninguna cubre a la otra:
//
//   a) El fragmento que pide ya no existe en el servidor. Como el hosting
//      reescribe todo lo no encontrado al index.html, la respuesta es un 200
//      con HTML donde se esperaba codigo. Mensaje MEDIDO en el navegador
//      (2026-08-31), no supuesto:
//        TypeError: Failed to fetch dynamically imported module: https://...
//      El patron anterior buscaba «Loading chunk N failed», que es el texto de
//      webpack: NUNCA casaba, asi que este manejador jamas se disparo.
//
//   b) La ruta ni siquiera existe en la configuracion que tiene cargada. El
//      menu viene de la BASE DE DATOS y ya conoce el modulo nuevo, pero el
//      router en memoria es el viejo: no hay ningun error que capturar, el
//      router simplemente cae en su comodin y pinta el 404. Ese caso se
//      detecta en `es-version-vieja.ts`, desde el propio componente de error.
//
// SEGURO ANTI-BUCLE: si la recarga no arregla el problema, recargar otra vez
// dejaria la pestana en un ciclo infinito. Se permite UNA sola recarga por
// pestana; la marca se limpia sola cuando la aplicacion lleva un rato
// funcionando, para que el siguiente despliegue vuelva a estar cubierto.
class ChunkErrorHandler implements ErrorHandler {
  handleError(error: any): void {
    const mensaje = String(error?.message ?? error ?? '');
    // Los tres motores dan un texto distinto para lo mismo, y el de webpack se
    // conserva por si alguna dependencia lo sigue lanzando.
    const esFragmentoQueYaNoExiste =
      /Failed to fetch dynamically imported module/i.test(mensaje) ||   // Chrome
      /error loading dynamically imported module/i.test(mensaje) ||     // Firefox
      /Importing a module script failed/i.test(mensaje) ||              // Safari
      /ChunkLoadError/i.test(mensaje) ||
      /Loading chunk [\d]+ failed/i.test(mensaje);

    if (esFragmentoQueYaNoExiste && recargarSiEsVersionVieja()) return;
    console.error(error);
  }
}

// Un import dinamico que falla llega como promesa rechazada y puede no pasar
// por el ErrorHandler de Angular.
window.addEventListener('unhandledrejection', event => {
  const mensaje = String((event as PromiseRejectionEvent)?.reason?.message ?? '');
  if (/dynamically imported module|ChunkLoadError|Loading chunk/i.test(mensaje)) {
    recargarSiEsVersionVieja();
  }
});
// ============================================================
// 🆕 FIN: Aplicacion desactualizada tras un despliegue
// ============================================================
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthInterceptor } from './app/pages/interceptors/auth.interceptor';

// ✅ i18n (ngx-translate v16) - config global; carga es.json/en.json desde assets/i18n
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { I18N_VERSION } from './app/app-version';

// Factory del loader HTTP (v16: el constructor acepta prefix/suffix).
// IMPORTANTE: prefijo ABSOLUTO ('/assets/...'). Con ruta relativa ('./assets/...')
// el navegador la resuelve contra la URL actual y en rutas profundas como
// /home/estados-mantenimiento pide /home/assets/i18n/es.json → 404 → se muestran
// las claves en vez del texto.
// SEGUNDA causa de claves crudas, medida el 2026-08-08: el servidor entrega
// /assets/i18n/*.json SIN Cache-Control, sin ETag y sin Last-Modified, asi que
// el navegador aplica cache heuristica y puede servir un diccionario viejo
// indefinidamente. Se agregaban claves, se regeneraba el JSON, y la pantalla
// seguia mostrando la CLAVE CRUDA aun recargando en duro.
//
// `I18N_VERSION` es la huella del contenido de los diccionarios, que genera
// scripts/build-i18n.cjs. Al ir en la URL, un diccionario nuevo es una URL
// nueva y el navegador no puede reutilizar el anterior. Cambia SOLO cuando
// cambian las traducciones, asi que no se pierde cache sin motivo.
export function translateHttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', `.json?v=${I18N_VERSION}`);
}

// ✅ PrimeNG imports (nuevo sistema de configuración)
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';

// ============================================================
// 🆕 INICIO: Guard plugin IFrame de AdminLTE - 2026-07-19
// ============================================================
// Bug conocido de AdminLTE 3.2: su plugin jQuery IFrame se dispara en clics
// del sidebar aunque la app NO use el modo iframe, y lanza
// "Cannot read properties of null (reading 'autoIframeMode')" (config null).
// F2RT no usa iframe-mode: se envuelve el plugin con un guard que absorbe el
// fallo sin alterar ningún comportamiento real. Reportado en consola por el
// usuario 2026-07-19. Autor: Claude Code
{
  const jq: any = (window as any).jQuery || (window as any).$;
  if (jq?.fn?.IFrame) {
    const originalIFrame = jq.fn.IFrame;
    jq.fn.IFrame = function (...args: unknown[]) {
      try {
        return originalIFrame.apply(this, args);
      } catch {
        return this; // modo iframe no usado: ignorar el bug de AdminLTE
      }
    };
    jq.fn.IFrame.Constructor = originalIFrame.Constructor;
    jq.fn.IFrame.noConflict = originalIFrame.noConflict;
  }
}
// ============================================================
// 🆕 FIN: Guard plugin IFrame de AdminLTE
// ============================================================

// ============================================================
// 🆕 INICIO: F2RT Custom Theme Preset - 2025-01-05
// ============================================================
// Preset personalizado basado en Aura con azul como color primario
// Autor: Claude Code
const F2RTPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#eef5fa',   // Lightest tint
      100: '#cfe1ef',
      200: '#a8c8e0',
      300: '#74a6cc',
      400: '#4088bb',
      500: '#80BA27',  // Base: Azul de marca VIVO
      600: '#175d90',
      700: '#134e78',
      800: '#103f61',
      900: '#0c2f49',
      950: '#081f31'   // Darkest shade
    },
    // ============================================================
    // 🆕 INICIO: Sizing canónico de campos small (form fields) - 2026-07-19
    // 🔧 RESTAURADO 2026-08-21: este bloque lo introdujo 30beb79e y lo borró
    // SIN QUERER a19d13dc ("avance cambio de logo") al sobrescribir main.ts
    // con una copia vieja. Sintoma medido: input/datepicker small a 34.3px y
    // 14px de fuente mientras p-select seguia en 28px (lo sostenia el CSS de
    // primeng-forms.sass). Es el desajuste que reporto el lider.
    // ============================================================
    // ALTURA CANÓNICA UNIFICADA F2RT = 28px para TODOS los campos small.
    //
    // Problema resuelto: Aura define `formField.sm` en 0.875rem / 0.375rem /
    // 0.625rem (→ input de texto renderizaba 34px). p-select y p-inputNumber
    // ya se forzaban a 0.75rem/28px con overrides !important en
    // primeng-forms.sass, pero input/multiselect/datepicker/textarea seguían
    // el default de Aura → los selects se veían MÁS PEQUEÑOS que los inputs.
    //
    // Solución (fuente única de verdad): bajar los tokens `formField.sm` para
    // que TODOS los campos con size/pSize="small" (input, select, multiselect,
    // datepicker, inputNumber, textarea) compartan font-size y padding y, por
    // tanto, la MISMA altura (~28px). No usa !important; los overrides previos
    // de primeng-forms.sass ahora coinciden con estos valores.
    // NOTA: NO afecta a p-button ni p-selectButton (usan tokens propios).
    // Ver Doc/PRIMENG-ECOSYSTEM/COMPONENTS/FORMS/PRIMENG-FORM-STANDARD.md §Sizing.
    // Autor: Claude Code
    formField: {
      sm: {
        fontSize: '0.75rem',    // 12px (antes 0.875rem = 14px)
        paddingX: '0.5rem',     // 8px  (antes 0.625rem = 10px)
        paddingY: '0.25rem',    // 4px  (antes 0.375rem = 6px) → altura ~28px
      },
    },
    // ============================================================
    // 🆕 FIN: Sizing canónico de campos small (form fields)
    // ============================================================
  }
});
// ============================================================
// 🆕 FIN: F2RT Custom Theme Preset
// ============================================================

// ✅ Módulos de terceros
import { ToastrModule } from 'ngx-toastr';
import { OAuthModule } from 'angular-oauth2-oidc';
import {
  NgxUiLoaderModule,
  NgxUiLoaderRouterModule,
  NgxUiLoaderHttpModule,
  NgxUiLoaderConfig,
  POSITION,
  SPINNER,
  PB_DIRECTION
} from 'ngx-ui-loader';

// ✅ App principal
import { AppComponent } from './app/app.component';
import { AppRoutingModule } from './app/app.routing.module';
import { SharedModule } from './app/components/shared/shared.module';
import { environment } from './environments/environment';

// ⚙️ Configuración del loader global
const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: '#80BA27', // 🔵 Azul de marca VIVO
  bgsOpacity: 0.9,
  bgsPosition: POSITION.bottomRight, // ✅ esquina inferior derecha
  bgsSize: 60,
  bgsType: SPINNER.cubeGrid,
  fgsType: SPINNER.cubeGrid,
  fgsPosition: POSITION.bottomRight, // ✅ también para el spinner principal
  pbColor: '#80BA27', // 🔵 Azul de marca VIVO
  hasProgressBar: false, // 🔥 oculta la barra superior
  blur: 0,
  overlayColor: 'rgba(255,255,255,0)',
  text: '', // sin texto
  textColor: '#80BA27' // 🔵 Azul de marca VIVO
};

// ⚙️ Modo producción
if (environment.production) {
  enableProdMode();
}

// 🚀 Bootstrap principal
bootstrapApplication(AppComponent, {
  providers: [
    provideAnimationsAsync(),
    providePrimeNG({
      zIndex: {
        modal: 1100,    // Default - el dialog mask/container
        overlay: 1200,  // Dropdowns, selects - SOBRE el dialog
        menu: 1200,     // Menus popup - SOBRE el dialog
        tooltip: 1300,  // Tooltips siempre arriba de todo
      },
      theme: {
        preset: F2RTPreset, // 🌈 Tema F2RT con azul como primario
        options: {
          darkModeSelector: false, // 🔒 Deshabilitar detección automática de modo oscuro (fix fondos negros)
        },
      },
    }),
    { provide: ErrorHandler, useClass: ChunkErrorHandler },
    provideHttpClient(withInterceptorsFromDi()),

    // i18n global (raíz): un único TranslateService para toda la app (público y privado).
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: translateHttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'es',
    }),

    // Auth Interceptor: maneja 401→logout, 403→notificación (Fase 5 auditoría seguridad)
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },

    // Timezone por defecto para pipes | date en templates HTML (Colombia UTC-5)
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: '-0500' } },

    importProvidersFrom(
      AppRoutingModule,
      SharedModule,
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-right',
        preventDuplicates: true,
      }),
      OAuthModule.forRoot(),
      // 🟢 Configuración final de loaders
      NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
      NgxUiLoaderRouterModule,
      NgxUiLoaderHttpModule.forRoot({ showForeground: false })
    ),
  ],
})
  // 🆕 2026-08-31: la aplicacion arranco. Si sigue en pie dentro de un rato, el
  // seguro anti-bucle se levanta para que el proximo despliegue tambien quede
  // cubierto (ver `services/recarga-por-version.ts`).
  .then(() => levantarSeguroTrasArranqueEstable())
  .catch(err => console.error(err));
